import type { RaidRole } from "@/shared/lib/roles";
import type { WowClass } from "@/shared/lib/wowClass";
import type { OverrideBody, SetupPlayer, StandInUpdate } from "./setup.types";

// One edit to a player, member or stand-in — the builders below turn it into
// the right full-state (LWW) write body. Both merge with explicit undefined
// checks, so only keys present in the change replace the current value.
export type PlayerChange = {
  wowClass?: WowClass | null;
  raidRole?: RaidRole | null;
  benched?: boolean;
};

// Member body: current delta + the change, clearing any half that matches the
// roster base (picking the base value means "no override").
export function memberOverrideBody(
  player: SetupPlayer,
  change: PlayerChange,
): OverrideBody {
  const wowClass =
    change.wowClass !== undefined
      ? change.wowClass
      : (player.overrideWowClass ?? null);
  const raidRole =
    change.raidRole !== undefined
      ? change.raidRole
      : (player.overrideRaidRole ?? null);
  return {
    wowClass: wowClass === player.baseWowClass ? null : wowClass,
    raidRole: raidRole === player.baseRaidRole ? null : raidRole,
    benched: change.benched ?? player.benched,
  };
}

// Stand-in vars: full current state + the change. The casts narrow the API's
// plain strings to the enums (DB check constraints only admit valid values);
// class/role stay null until filled in.
export function standInUpdate(
  player: SetupPlayer,
  change: PlayerChange & { name?: string },
): StandInUpdate {
  return {
    id: player.id,
    name: change.name !== undefined ? change.name : player.name,
    wowClass:
      change.wowClass !== undefined
        ? change.wowClass
        : (player.wowClass as WowClass | null),
    raidRole:
      change.raidRole !== undefined
        ? change.raidRole
        : (player.raidRole as RaidRole | null),
    benched: change.benched ?? player.benched,
  };
}

// Optimistic twins of the write builders above: predict the server row after the write so the
// patched cache equals the next poll's data. Keep in lockstep with memberOverrideBody/standInUpdate.
export function applyOverride(p: SetupPlayer, body: OverrideBody): SetupPlayer {
  return {
    ...p,
    wowClass: body.wowClass ?? p.baseWowClass,
    raidRole: body.raidRole ?? p.baseRaidRole,
    overrideWowClass: body.wowClass,
    overrideRaidRole: body.raidRole,
    benched: body.benched,
  };
}

export function applyStandInUpdate(
  p: SetupPlayer,
  vars: StandInUpdate,
): SetupPlayer {
  return {
    ...p,
    name: vars.name,
    wowClass: vars.wowClass,
    raidRole: vars.raidRole,
    benched: vars.benched,
  };
}
